import { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';
import CurrencyInput from 'react-currency-input-field';

import db from '../../db/db';
import { setAlert } from '../../actions/alert';
import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        background: '#1F1F1F',
        borderColor: state.isFocused ? '#F5C800' : '#2E2E2E',
        boxShadow: state.isFocused ? '0 0 0 2px rgba(245,200,0,0.15)' : 'none',
        borderRadius: '10px',
        minHeight: '50px',
        '&:hover': { borderColor: '#F5C800' },
    }),
    menu: (base) => ({
        ...base,
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '10px',
        zIndex: 99,
    }),
    option: (base, state) => ({
        ...base,
        background: state.isSelected ? 'rgba(245,200,0,0.15)' : state.isFocused ? '#222' : 'transparent',
        color: state.isSelected ? '#F5C800' : '#C0C0C0',
        cursor: 'pointer',
    }),
    singleValue: (base) => ({ ...base, color: '#F0F0F0' }),
    placeholder: (base) => ({ ...base, color: '#555' }),
    input: (base) => ({ ...base, color: '#F0F0F0' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#555' }),
    clearIndicator: (base) => ({ ...base, color: '#555' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const Gasto = () => {
    const dispatch = useDispatch();

    const [empleadoLogueado, setEmpleadoLogueado] = useState(null);
    const [eventoActivo, setEventoActivo] = useState(null);
    const [proveedores, setProveedores] = useState([]);
    const [metodos, setMetodos] = useState([]);

    const [proveedorData, setProveedorData] = useState(null);
    const [metodoData, setMetodoData] = useState(null);
    const [total, setTotal] = useState('');
    const [concepto, setConcepto] = useState('');

    const [statusGuardar, setStatusGuardar] = useState(false);
    const [totalFocused, setTotalFocused] = useState(false);

    useEffect(() => {
        const init = async () => {
            const sesion = await db.sesion.toArray();
            const logueado = sesion[0];
            setEmpleadoLogueado(logueado);

            const sucursalId = logueado.sucursal?._id || logueado.sucursal;
            const sucursalReg = await db.sucursales
                .filter(s => String(s._id || s.id) === String(sucursalId))
                .first();

            const provs = await db.proveedores.toArray();
            const mets  = await db.metodos.toArray();

            setProveedores(
                provs
                    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
                    .map(p => ({ value: p._id, label: p.nombre, ...p }))
            );
            setMetodos(mets.map(m => ({ value: m._id, label: m.nombre, ...m })));
            setEventoActivo(sucursalReg?.evento || null);
        };

        init();
    }, []);

    const handleTotalChange = (value) => {
        setTotal(value);
    };

    const handleGuardar = async () => {
        if (!proveedorData) return dispatch(setAlert('Selecciona un proveedor', 'danger'));
        if (!metodoData)    return dispatch(setAlert('Selecciona un método de pago', 'danger'));
        if (!total || isNaN(parseFloat(total)) || parseFloat(total) <= 0)
            return dispatch(setAlert('Ingresa un total válido', 'danger'));
        if (!concepto.trim()) return dispatch(setAlert('Ingresa un concepto', 'danger'));

        setStatusGuardar(true);

        try {
            await db.gastos.add({
                sucursal:     empleadoLogueado.sucursal?._id || empleadoLogueado.sucursal,
                responsable:  empleadoLogueado._id,
                proveedor:    proveedorData._id,
                metodo:       metodoData._id,
                total:        parseFloat(total),
                concepto:     concepto.trim(),
                evento:       eventoActivo?._id || null,
                fecha:        new Date(), 
                status:       true,
                adeudado:     0,
                remobolsado:  1
            });

            dispatch(setAlert('Gasto registrado correctamente', 'success'));
            setProveedorData(null);
            setMetodoData(null);
            setTotal('');
            setConcepto('');
        } catch {
            dispatch(setAlert('Error al registrar el gasto', 'danger'));
        } finally {
            setStatusGuardar(false);
        }
    };

    const handleCancelar = () => {
        setProveedorData(null);
        setMetodoData(null);
        setTotal('');
        setConcepto('');
    };

    const formLleno = proveedorData && metodoData && total && concepto.trim();

    return (
        <div style={styles.page}>
            <div style={styles.header}>
                <i className="fas fa-file-invoice-dollar me-3" style={{ color: '#F5C800', fontSize: '1.3rem' }}></i>
                <h4 style={styles.titulo}>Nuevo gasto</h4>
            </div>

            <div style={styles.card}>
                {/* Proveedor */}
                <div style={styles.campo}>
                    <label style={styles.label}>Proveedor</label>
                    <Select
                        options={proveedores}
                        value={proveedorData}
                        onChange={setProveedorData}
                        isClearable
                        isSearchable={false}
                        placeholder="Selecciona un proveedor"
                        styles={selectStyles}
                        components={{ ValueContainer: CustomValueContainer }}
                        menuPortalTarget={document.body}
                    />
                </div>

                {/* Método de pago */}
                <div style={styles.campo}>
                    <label style={styles.label}>Método de pago</label>
                    <Select
                        options={metodos}
                        value={metodoData}
                        onChange={setMetodoData}
                        isClearable
                        isSearchable={false}
                        placeholder="Selecciona un método"
                        styles={selectStyles}
                        components={{ ValueContainer: CustomValueContainer }}
                        menuPortalTarget={document.body}
                    />
                </div>

                <div style={styles.campo}>
                    <label style={styles.label}>Total</label>
                    <CurrencyInput
                        id="total"
                        name="total"
                        value={total || ''}
                        onValueChange={handleTotalChange}
                        prefix="$"
                        decimalsLimit={2}
                        groupSeparator=","
                        decimalSeparator="."
                        placeholder="$0.00"
                        onFocus={() => setTotalFocused(true)}
                        onBlur={() => setTotalFocused(false)}
                        style={{
                            background: '#1F1F1F',
                            border: `2px solid ${totalFocused ? '#F5C800' : '#2E2E2E'}`,
                            boxShadow: totalFocused ? '0 0 0 2px rgba(245,200,0,0.15)' : 'none',
                            borderRadius: '10px',
                            padding: '0.85rem 1rem',
                            color: '#F0F0F0',
                            fontSize: '1rem',
                            fontWeight: 600,
                            width: '100%',
                            outline: 'none',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            fontFamily: 'inherit',
                        }}
                    />
                </div>

                {/* Concepto */}
                <div style={styles.campo}>
                    <label style={styles.label}>Concepto</label>
                    <textarea
                        value={concepto}
                        onChange={e => setConcepto(e.target.value)}
                        placeholder="Describe el motivo del gasto..."
                        rows={4}
                        style={styles.textarea}
                    />
                </div>

                {/* Botones */}
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                    <button
                        style={!proveedorData && !metodoData && !total && !concepto
                            ? { ...styles.btnCancelar, opacity: 0.35, cursor: 'not-allowed' }
                            : styles.btnCancelar
                        }
                        onClick={handleCancelar}
                        disabled={!proveedorData && !metodoData && !total && !concepto}
                    >
                        <i className="fas fa-times me-2"></i>
                        Cancelar
                    </button>
                    <button
                        style={statusGuardar || !formLleno
                            ? { ...styles.btnGuardar, opacity: 0.5, cursor: 'not-allowed' }
                            : styles.btnGuardar
                        }
                        onClick={handleGuardar}
                        disabled={statusGuardar || !formLleno}
                    >
                        {statusGuardar ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="fas fa-save me-2"></i>
                                Guardar gasto
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

const styles = {
    page: {
        maxWidth: '560px',
        margin: '0 auto',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1.5rem',
        paddingBottom: '1rem',
        borderBottom: '1px solid #2E2E2E',
    },
    titulo: {
        color: '#F0F0F0',
        fontWeight: 700,
        margin: 0,
    },
    card: {
        background: '#1A1A1A',
        border: '1px solid #2E2E2E',
        borderRadius: '14px',
        padding: '1.75rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '1.25rem',
    },
    campo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    label: {
        color: '#C0C0C0',
        fontSize: '0.9rem',
        fontWeight: 600,
    },
    inputMonedaWrap: {
        display: 'flex',
        alignItems: 'center',
        background: '#1F1F1F',
        border: '2px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0 1rem',
        minHeight: '50px',
        transition: 'border-color 0.2s',
    },
    inputMonedaSymbol: {
        color: '#555',
        fontSize: '1.1rem',
        fontWeight: 700,
        marginRight: '0.5rem',
        flexShrink: 0,
    },
    inputMoneda: {
        background: 'transparent',
        border: 'none',
        outline: 'none',
        color: '#F0F0F0',
        fontSize: '1.1rem',
        fontWeight: 600,
        width: '100%',
    },
    textarea: {
        background: '#1F1F1F',
        border: '2px solid #2E2E2E',
        borderRadius: '10px',
        padding: '0.85rem 1rem',
        color: '#F0F0F0',
        fontSize: '0.95rem',
        resize: 'vertical',
        outline: 'none',
        fontFamily: 'inherit',
        lineHeight: 1.5,
    },
    btnCancelar: {
        flex: 1,
        background: '#eb3508',
        color: '#ffffff',
        border: 'none',
        borderRadius: '10px',
        padding: '0.85rem',
        fontWeight: 800,
        fontSize: '1rem',
        cursor: 'pointer',
    },
    btnGuardar: {
        flex: 2,
        background: '#F5C800',
        color: '#111111',
        border: 'none',
        borderRadius: '10px',
        padding: '0.85rem',
        fontWeight: 800,
        fontSize: '1rem',
        cursor: 'pointer',
    },
};

export default Gasto;
